// src/router/routes.js
//
// 🎯 COMO MUDAR A ORDEM DOS MENUS:
// ================================
// 1. A ordem no array abaixo = ordem no menu lateral
// 2. Para esconder do menu: coloque `label: null`
// 3. Para mudar permissão: altere o `minLevel`:
//    - ADMIN_LEVELS.BASIC (1)  = Todos os admins
//    - ADMIN_LEVELS.SUPER (2)  = Super Admin+
//    - ADMIN_LEVELS.MASTER (4) = Master Admin+
//    - ADMIN_LEVELS.GOD (99)   = Só God Mode
//
// 📌 Rotas com label: null não aparecem no menu, mas são acessíveis via URL
//
// 🗂️ GRUPOS DE MENU (submenu):
//    Um item com `children` vira um grupo expansível na sidebar (igual ao Regula
//    Hub). Filhos com `path` ABSOLUTO (começa com "/") são grupos PURAMENTE de
//    menu — a URL do filho NÃO muda (ex.: Gestão > Pacientes continua /patients,
//    não /gestao/patients). Filhos com path relativo aninham sob o pai (regula-hub).
//    O `path` do grupo (ex.: /operacao) é só uma chave de menu — não vira rota.
//

import { ADMIN_LEVELS } from "@/constants/adminLevels";

export const appRoutes = [
  // ═══════════════════════════════════════════════════════════════
  // 0️⃣ HOME - Sempre primeiro
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/home",
    name: "HomeView",
    component: () => import("@/views/Home.vue"),
    label: "Início",
    icon: "fas fa-house",
    minLevel: ADMIN_LEVELS.BASIC,
  },

  // ═══════════════════════════════════════════════════════════════
  // 1️⃣ OPERAÇÃO — fila, dashboard e presença (grupo de menu)
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/operacao",
    name: "OperacaoGroup",
    label: "Operação",
    icon: "fas fa-headset",
    minLevel: ADMIN_LEVELS.BASIC,
    children: [
      {
        path: "/live-queue-monitor",
        name: "LiveQueueMonitorView",
        component: () => import("@/views/LiveQueueMonitorV2.vue"),
        label: "Fila de Atendimento",
        icon: "fas fa-users-line",
        minLevel: ADMIN_LEVELS.BASIC,
      },
      {
        // Calendário geral da rede (desdobramento da PRD-062). Mora em Operação,
        // colado na Fila de Atendimento: as duas respondem "o que está
        // acontecendo na rede" — uma agora, a outra na semana.
        path: "/operacao/agenda",
        name: "AgendaOverviewView",
        component: () => import("@/views/AgendaOverview.vue"),
        label: "Agenda",
        icon: "fas fa-calendar-week",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/operational-dashboard",
        name: "OperationalDashboardView",
        component: () => import("@/views/OperationalDashboard.vue"),
        label: "Dashboard",
        icon: "fas fa-chart-column",
        minLevel: ADMIN_LEVELS.BASIC,
      },
      // PRD-046 — Busca Ativa: a fila de quem não foi atendido e precisa de
      // contato. Vem ANTES do Histórico de propósito: esta é a fila de trabalho
      // do dia; o Histórico é a consulta de quem já passou por ela.
      //
      // ⛔ O label é "Busca Ativa", NUNCA "Repescagem" — Repescagem é a tela do
      // Regula Hub (F-10, aposentada em D-008 e redirecionada para Baixa
      // Automática). Dois nomes iguais para coisas diferentes no mesmo sidebar.
      {
        path: "/attendance-recall",
        name: "AttendanceRecallView",
        component: () => import("@/views/AttendanceRecall.vue"),
        label: "Busca Ativa",
        icon: "fas fa-phone-volume",
        // Mesmo patamar do Histórico: ver a fila é BasicAdmin. REGISTRAR contato
        // também é BasicAdmin (Q-9) — é o trabalho das assistentes —, e o
        // backend cobra o nível de novo em cada escrita.
        minLevel: ADMIN_LEVELS.BASIC,
      },
      {
        path: "/operacao/repescagem",
        name: "PatientRecallView",
        component: () => import("@/views/PatientRecall.vue"),
        label: "Repescagem de Faltosos",
        icon: "fas fa-calendar-plus",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      // PRD-043 Fase 1 — histórico de atendimentos, somente leitura.
      // SUPER: a tela é cross-HC por natureza (expõe paciente e médico de todos
      // os tenants), o mesmo patamar que o backend exige via IHcScopeGuard.
      {
        path: "/attendance-history",
        name: "AttendanceHistoryView",
        component: () => import("@/views/AttendanceHistory.vue"),
        label: "Histórico de Atendimentos",
        icon: "fas fa-clock-rotate-left",
        // Ver a fila é BasicAdmin; REGISTRAR desfecho é MasterAdmin, checado no
        // botão (canRegisterAssisted) e de novo no backend.
        minLevel: ADMIN_LEVELS.BASIC,
      },
      // PRD-030 — produtividade médica hora a hora (base de faturamento).
      // MASTER: a tela expõe identidade do médico (CPF/CRM) + a base do que é
      // faturado, mesmo patamar de /doctors e /patients.
      {
        path: "/doctor-productivity",
        name: "DoctorProductivityHourlyView",
        component: () => import("@/views/DoctorProductivityHourly.vue"),
        label: "Produtividade por Hora",
        icon: "fas fa-hourglass-half",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      // PRD-051 — painel agregado do período, IRMÃO da visão horária acima (que
      // NÃO muda). A horária audita um caso — um médico, um dia; esta responde
      // "quem fez o quê no período", com ranking e filtro. Mesmo patamar MASTER:
      // expõe identidade médica e a base do que é faturado.
      {
        path: "/doctor-productivity-panel",
        name: "DoctorProductivityPanelView",
        component: () => import("@/views/DoctorProductivityPanel.vue"),
        label: "Produtividade Médica",
        icon: "fas fa-chart-pie",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/online-users",
        name: "OnlineUsersView",
        component: () => import("@/views/OnlineUsersManagement.vue"),
        label: "Usuários Online",
        icon: "fas fa-signal",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/registration-diagnostic",
        name: "RegistrationDiagnosticView",
        component: () => import("@/views/RegistrationDiagnostic.vue"),
        label: "Diagnóstico de Cadastro",
        icon: "fas fa-stethoscope",
        // Tela liberada por inteiro para operação: diagnosticar E limpar rodam
        // com BasicAdmin. Não há mais gate por nível dentro da view — o backend
        // (AdminPatientManagementService.EnsureAdminAsync) cobra o mesmo patamar
        // no diagnose e no cleanup, e loga quem executou cada limpeza.
        minLevel: ADMIN_LEVELS.BASIC,
      },
      {
        path: "/audit-logs",
        name: "AuditLogsView",
        component: () => import("@/views/AuditLogs.vue"),
        label: "Logs de Auditoria",
        icon: "fas fa-clipboard-list",
        minLevel: ADMIN_LEVELS.MASTER,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // 2️⃣ GESTÃO — pacientes, médicos e administradores (grupo de menu)
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/gestao",
    name: "GestaoGroup",
    label: "Gestão",
    icon: "fas fa-users-gear",
    minLevel: ADMIN_LEVELS.MASTER,
    children: [
      {
        path: "/patients",
        name: "PatientsView",
        component: () => import("@/views/PatientsManagement.vue"),
        label: "Pacientes",
        icon: "fas fa-hospital-user",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/doctors",
        name: "DoctorsView",
        component: () => import("@/views/DoctorsManagement.vue"),
        label: "Médicos",
        icon: "fas fa-user-doctor",
        // PRD-020 RF-5: paridade com /patients (MASTER). Gate real é backend
        // (EnsureMasterAdmin); ações destrutivas seguem em níveis altos via v-permission.
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/general-admins",
        name: "GeneralAdminsView",
        component: () => import("@/views/GeneralAdmins.vue"),
        label: "Administradores",
        icon: "fas fa-users-cog",
        minLevel: ADMIN_LEVELS.MASTER,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // 3️⃣ AGENDA — agendamentos, escalas e auditoria (grupo de menu)
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/agenda",
    name: "AgendaGroup",
    label: "Agenda",
    icon: "fas fa-calendar-days",
    minLevel: ADMIN_LEVELS.MASTER,
    children: [
      {
        path: "/Appointments",
        name: "AppointmentsView",
        component: () => import("@/views/Appointments.vue"),
        label: "Agendamentos",
        icon: "fas fa-calendar-check",
        minLevel: ADMIN_LEVELS.GOD,
      },
      {
        path: "/schedules",
        name: "ScheduleViewerView",
        component: () => import("@/views/ScheduleViewer.vue"),
        label: "Escalas Médicas",
        icon: "fas fa-calendar-alt",
        minLevel: ADMIN_LEVELS.GOD,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // 💬 MENSAGERIA — disparos por WhatsApp (PRD-060, grupo de menu)
  // ═══════════════════════════════════════════════════════════════
  // Grupo puro de menu, como Operação/Gestão/Agenda: sem component/redirect,
  // "/messaging" não é registrado no router e cai no coringa → "/" → "/home".
  // Só Master Admin+ (RF-5). As quatro telas são vazias nesta PRD.
  {
    path: "/messaging",
    name: "MessagingGroup",
    // "SP": the admin serves several HCs, and every Mensageria screen is the HC-SP pipeline's (22/10/2026).
    label: "Mensageria SP",
    icon: "fab fa-whatsapp",
    minLevel: ADMIN_LEVELS.MASTER,
    children: [
      // Dashboard first (the overview); the other screens A–Z (22/10/2026, operator's request).
      {
        path: "/messaging/dashboard",
        name: "MessagingDashboardView",
        component: () => import("@/views/messaging/MessagingDashboard.vue"),
        label: "Dashboard",
        icon: "fas fa-chart-column",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/messaging/alerts",
        name: "OperationalAlertsView",
        component: () => import("@/views/messaging/OperationalAlerts.vue"),
        // Alertas operacionais da pipeline do HC-SP no Slack (22/10/2026); todos nascem desligados.
        label: "Alertas",
        icon: "fas fa-bell",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/messaging/campaigns",
        name: "CampaignsView",
        component: () => import("@/views/messaging/HealthCampaigns.vue"),
        // Calendário de campanhas de saúde da pipeline do HC-SP, mês a mês (22/10/2026).
        label: "Campanhas",
        icon: "fas fa-bullhorn",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/messaging/dispatch-controls",
        name: "DispatchControlsView",
        component: () => import("@/views/messaging/DispatchControls.vue"),
        // Liga/desliga cada envio de WhatsApp da pipeline do HC-SP (14/10/2026).
        label: "Controles de disparo",
        icon: "fas fa-toggle-on",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/messaging/demand-vs-slots",
        name: "DemandVsSlotsView",
        component: () => import("@/views/messaging/DemandVsSlots.vue"),
        // 29/10/2026: the queue sieved down to who waits for a slot, against the free slots, per specialty
        // (the "Forecast e Peneira de Vagas versus Fila" sheet as a live screen).
        label: "Demanda × Vagas",
        icon: "fas fa-scale-balanced",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/messaging/template-dispatch",
        name: "TemplateDispatchView",
        component: () => import("@/views/messaging/TemplateDispatch.vue"),
        // PRD-060 RF-6 / PRD-061 (ajuste 2026-09-21): a tela é o disparo genérico por template —
        // catálogo em src/services/templateDispatchService.js (hoje: lembrete de consulta de
        // especialidade). O rótulo "Disparos HSPM" (RF-9) foi revertido.
        label: "Disparo por Template",
        icon: "fas fa-paper-plane",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/messaging/specialty-queue",
        name: "SpecialtyQueueView",
        component: () => import("@/views/messaging/SpecialtyQueue.vue"),
        label: "Fila de Especialidade",
        icon: "fas fa-list-ol",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/messaging/care-lines",
        name: "CareLinesView",
        component: () => import("@/views/messaging/CareLines.vue"),
        label: "Linhas de Cuidado",
        icon: "fas fa-route",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "/messaging/ps-digital",
        name: "PsDigitalView",
        component: () => import("@/views/messaging/PsDigital.vue"),
        // 30/10/2026: the Pronto Socorro Digital journey (06) — from the request the doctor could not complete
        // to the patient's answer to the invite ("Ainda preciso" / "Não preciso mais"), plus today's selection.
        label: "Pronto Socorro Digital",
        icon: "fas fa-truck-medical",
        minLevel: ADMIN_LEVELS.MASTER,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // 4️⃣ REGULA HUB — painel administrativo (PRD-002 RF-19)
  // ═══════════════════════════════════════════════════════════════
  // Grupo expansível com children RELATIVOS (URLs /regula-hub/*). MainLayout
  // renderiza recursivamente. Estado expand/collapse persistido em localStorage.
  {
    path: "/regula-hub",
    name: "RegulaHubGroup",
    label: "Regula Hub",
    icon: "fas fa-hospital",
    minLevel: ADMIN_LEVELS.MASTER,
    redirect: "/regula-hub/pipeline",
    children: [
      // PRD-002 / DT-009 — Pipeline de integração regulador → sistema
      // de atendimento (hoje SISReg III → Saúde AM Digital). Substituiu a
      // antiga "tela suprema" (removida em 2026-05-16) que tinha conceito
      // certo (pipelines) mas UX confusa.
      {
        path: "pipeline",
        name: "RegulaHubPipelineView",
        component: () => import("@/views/regula-hub/RegulaHubPipeline.vue"),
        label: "Pipeline",
        icon: "fas fa-diagram-project",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "dashboard",
        name: "RegulaHubDashboardView",
        component: () => import("@/views/regula-hub/RegulaHubDashboard.vue"),
        label: "Visão Geral",
        icon: "fas fa-chart-line",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "appointments",
        name: "RegulaHubAppointmentsView",
        component: () => import("@/views/regula-hub/RegulaHubAppointments.vue"),
        label: "Agendamentos",
        icon: "fas fa-calendar-check",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      // PRD-002 — Baixa Automática: acompanhamento das baixas de agendamentos
      // realizados + repescagem das faltas teóricas (remarcação manual).
      {
        path: "discharge",
        name: "RegulaHubDischargeView",
        component: () => import("@/views/regula-hub/RegulaHubDischarge.vue"),
        label: "Baixa Automática",
        icon: "fas fa-clipboard-check",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      // PRD-002 / D-008 — "Repescagem" aposentada por redirect (deep-links antigos
      // continuam funcionando). Sem label = fora do menu. A tela única "Baixa
      // Automática" (/regula-hub/discharge) absorveu o funil + a lista de
      // repescagem. Mesmo padrão de recoverable/alerts → action-center.
      {
        path: "repescagem",
        redirect: "/regula-hub/discharge",
      },
      // PRD-002 / D-006 (RF-34) — Central de Ações: fila única priorizada
      // que agrega todas as pendências que exigem ação humana, resolvíveis
      // inline. Substitui "Para Recuperar" (redirect abaixo) por unificação.
      {
        path: "action-center",
        name: "RegulaHubActionCenterView",
        component: () => import("@/views/regula-hub/RegulaHubActionCenter.vue"),
        label: "Central de Ações",
        icon: "fas fa-inbox",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      // CA-7 — "Para Recuperar" aposentada por redirect (deep-links antigos
      // continuam funcionando). Sem label = fora do menu (a Central a substitui).
      {
        path: "recoverable",
        redirect: "/regula-hub/action-center",
      },
      // DT-012 — "Alertas" aposentada por redirect (deep-links antigos continuam
      // funcionando). Sem label = fora do menu. A fonte que populava a tabela
      // `alerts` (publish nos jobs de pipeline) foi removida no hub em 27/05
      // (commit 6117711); a Central de Ações (D-006) a substitui.
      {
        path: "alerts",
        redirect: "/regula-hub/action-center",
      },
      // PRD-002 — gerenciamento de mapeamentos (solicitante→executante + unidades)
      {
        path: "mappings",
        name: "RegulaHubMappingsView",
        component: () => import("@/views/regula-hub/RegulaHubMappings.vue"),
        label: "Mapeamentos",
        icon: "fas fa-sitemap",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      {
        path: "credentials",
        name: "RegulaHubCredentialsView",
        component: () => import("@/views/regula-hub/RegulaHubCredentials.vue"),
        label: "Credenciais",
        icon: "fas fa-key",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      // Próximos: Configuração
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // 🔐 SEGURANÇA — gate de "senha de operação" (auditoria + rotação)
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/seguranca",
    name: "SegurancaGroup",
    label: "Segurança",
    icon: "fas fa-shield-halved",
    minLevel: ADMIN_LEVELS.MASTER,
    children: [
      {
        path: "/operation-gate/audit",
        name: "OperationGateAuditView",
        component: () => import("@/views/OperationGateAudit.vue"),
        label: "Auditoria",
        icon: "fas fa-user-shield",
        minLevel: ADMIN_LEVELS.MASTER,
      },
      // Rotação da palavra: só God. O filho fica oculto no menu para Master
      // (visibleChildren filtra por child.minLevel); a rota é God-gated.
      {
        path: "/operation-gate/rotate",
        name: "OperationSecretRotationView",
        component: () => import("@/views/OperationSecretRotation.vue"),
        label: "Rotacionar senha",
        icon: "fas fa-key",
        minLevel: ADMIN_LEVELS.GOD,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // 🔧 CONFIGURAÇÕES - Sempre no final (separado visualmente)
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/settings",
    name: "SettingsView",
    component: () => import("@/views/SettingsView.vue"),
    label: "Configurações",
    icon: "fas fa-cog",
    minLevel: ADMIN_LEVELS.BASIC,
  },

  // ═══════════════════════════════════════════════════════════════
  // 🔒 ROTAS OCULTAS (não aparecem no menu)
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/operations-monitor",
    name: "OperationsMonitorView",
    component: () => import("@/views/OperationsMonitor.vue"),
    label: null, // Oculto - acesso direto via URL (God Mode)
    icon: null,
    minLevel: ADMIN_LEVELS.GOD,
  },
  {
    path: "/patients/:id",
    name: "PatientDetailsView",
    component: () => import("@/views/PatientDetails.vue"),
    label: null, // Oculto - acessível via clique na tabela de pacientes
    icon: null,
    minLevel: ADMIN_LEVELS.MASTER,
  },
  {
    path: "/LiveQueueLogs",
    name: "LiveQueueLogsView",
    component: () => import("@/views/LiveQueueLogs.vue"),
    label: null, // Oculto - funcionalidade consolidada na Fila de Atendimento
    icon: null,
    minLevel: ADMIN_LEVELS.BASIC,
  },
  {
    path: "/appointments/new",
    name: "AppointmentWizardView",
    component: () => import("@/views/AppointmentWizard.vue"),
    label: null, // Oculto - acessível via botão na página de Agendamentos
    icon: null,
    minLevel: ADMIN_LEVELS.GOD,
  },
  {
    path: "/schedules/create",
    name: "ScheduleCreatorView",
    component: () => import("@/views/ScheduleCreator.vue"),
    label: null, // Oculto - acessível via botão na página de Escalas
    icon: null,
    minLevel: ADMIN_LEVELS.MASTER,
  },
  {
    path: "/doctors/bulk-import",
    name: "DoctorsBulkImportView",
    component: () => import("@/views/DoctorsBulkImport.vue"),
    label: null, // Oculto - acessível via botão em Gestão de Médicos (PRD-031)
    icon: null,
    minLevel: ADMIN_LEVELS.MASTER,
  },
  {
    path: "/me",
    name: "MyProfileView",
    component: () => import("@/views/MyProfile.vue"),
    label: null, // Oculto - acessível via clique no avatar do usuário
    icon: null,
    minLevel: ADMIN_LEVELS.BASIC,
  },
  {
    path: "/live-queue-monitor-v1",
    name: "LiveQueueMonitorV1View",
    component: () => import("@/views/LiveQueueMonitor.vue"),
    label: null, // Oculto - V1 fallback, acesso direto via URL
    icon: null,
    minLevel: ADMIN_LEVELS.BASIC,
  },
];
